import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getTrips } from '../store/tripSlice';
import TripCard from '../components/TripCard';
import tripService from '../services/tripService';
import authService from '../services/authService';
import { Heart, Search } from 'lucide-react';

const Favorites = () => {
  const dispatch = useDispatch();
  const { trips, loading } = useSelector(state => state.trips);
  const { user } = useSelector(state => state.auth);
  
  const [favorites, setFavorites] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTrips, setFilteredTrips] = useState([]);

  useEffect(() => {
    dispatch(getTrips({}));
  }, [dispatch]);

  useEffect(() => {
    if (user?.favoriteTrips) {
      setFavorites(new Set(user.favoriteTrips));
    }
  }, [user]);

  useEffect(() => {
    // Reload user data to get updated favorites
    const loadUserData = async () => {
      try {
        const userData = await authService.loadUser();
        if (userData?.favoriteTrips) {
          setFavorites(new Set(userData.favoriteTrips));
        }
      } catch (error) {
        console.log('Error loading user data');
      }
    };
    loadUserData();
  }, []);

  useEffect(() => {
    const favoriteTrips = trips.filter(trip => favorites.has(trip._id));
    const filtered = favoriteTrips.filter(trip =>
      trip.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      trip.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredTrips(filtered);
  }, [trips, favorites, searchTerm]);

  const handleToggleFavorite = async (tripId) => {
    try {
      const result = await tripService.toggleFavorite(tripId);
      // Reload user data to get updated favorites
      const userData = await authService.loadUser();
      if (userData?.favoriteTrips) {
        setFavorites(new Set(userData.favoriteTrips));
      }
      
      if (result.isFavorite) {
        toast.success('Added to favorites! ❤️');
      } else {
        toast.success('Removed from favorites! 💔');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-indigo-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12 animate-fade-in">
          <div className="flex justify-center items-center mb-4">
            <Heart className="h-12 w-12 text-red-500 mr-4 animate-pulse" />
            <h1 className="text-4xl md:text-5xl font-bold text-rainbow">
              My Favorite Trips
            </h1>
            <Heart className="h-12 w-12 text-red-500 ml-4 animate-pulse" />
          </div>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Your collection of amazing travel experiences and dream destinations ✨
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-md mx-auto mb-8 animate-slide-up">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search your favorite trips... 🔍"
              className="w-full pl-12 pr-4 py-4 bg-white/80 backdrop-blur-md border border-purple-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent shadow-lg transition-all duration-300"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-r from-pink-400 to-red-500 rounded-2xl p-6 text-white text-center shadow-xl animate-bounce-in">
            <div className="text-3xl font-bold">{favorites.size}</div>
            <div className="text-pink-100">❤️ Favorite Trips</div>
          </div>
          <div className="bg-gradient-to-r from-purple-400 to-indigo-500 rounded-2xl p-6 text-white text-center shadow-xl animate-bounce-in" style={{animationDelay: '0.1s'}}>
            <div className="text-3xl font-bold">{filteredTrips.length}</div>
            <div className="text-purple-100">🔍 Search Results</div>
          </div>
          <div className="bg-gradient-to-r from-orange-400 to-pink-500 rounded-2xl p-6 text-white text-center shadow-xl animate-bounce-in" style={{animationDelay: '0.2s'}}>
            <div className="text-3xl font-bold">{trips.length}</div>
            <div className="text-orange-100">🌍 Total Trips</div>
          </div>
        </div>

        {/* Trips Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-500 border-t-transparent"></div>
          </div>
        ) : filteredTrips.length === 0 ? (
          <div className="text-center py-16 animate-fade-in">
            <div className="text-8xl mb-6">💔</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {favorites.size === 0 ? 'No favorites yet!' : 'No matching favorites'}
            </h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              {favorites.size === 0 
                ? 'Start exploring trips and add them to your favorites by clicking the heart icon ❤️'
                : 'Try adjusting your search terms to find your favorite trips 🔍'
              }
            </p>
            <button 
              onClick={() => window.location.href = '/dashboard'}
              className="btn-primary"
            >
              🌟 Explore Trips
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredTrips.map((trip, index) => (
              <div 
                key={trip._id}
                className="animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <TripCard
                  trip={trip}
                  onToggleFavorite={handleToggleFavorite}
                  isFavorite={favorites.has(trip._id)}
                />
              </div>
            ))}
          </div>
        )}

        {/* Fun Facts */}
        {filteredTrips.length > 0 && (
          <div className="mt-16 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-3xl p-8 text-white text-center animate-fade-in">
            <h3 className="text-2xl font-bold mb-4">🎉 Your Travel Stats</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className="text-3xl font-bold">
                  {filteredTrips.reduce((total, trip) => total + trip.totalDays, 0)}
                </div>
                <div className="text-indigo-100">📅 Total Days Planned</div>
              </div>
              <div>
                <div className="text-3xl font-bold">
                  {filteredTrips.filter(trip => trip.participants.length > 0).length}
                </div>
                <div className="text-purple-100">👥 Collaborative Trips</div>
              </div>
              <div>
                <div className="text-3xl font-bold">
                  {filteredTrips.filter(trip => trip.isPublic).length}
                </div>
                <div className="text-pink-100">🌐 Public Trips</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Favorites;